// src/belediye/pages/sistem/SistemMahallelerPage.js
//
// FAZ-24 BLOK-B.3 — Mahalleler yönetim ekranı (liste + ekle/düzenle/sil).
// Model tek alan: { id, name }. Ekle/Düzenle tek modal (create=id yok, edit=id var);
// Sil düzenleme modalında onaylı. Gate: neighborhoods.view (route), .create/.update/.delete
// (aksiyon). Sistem workspace yalnız uiOn açıkken erişilir → doğrudan can() (çift-yol yok).

import React, { useEffect, useMemo, useState } from "react";
import { PlusIcon, MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import {
  fetchNeighborhoods,
  createNeighborhood,
  updateNeighborhood,
  deleteNeighborhood,
} from "../../services/yonetimApi";
import "./yonetim-ekrani.css";

const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};

export default function SistemMahallelerPage() {
  const { can } = usePermissions();
  const canCreate = can("neighborhoods.create");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null=kapalı, {}=yeni, {id,..}=düzenle

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetchNeighborhoods();
      setRows(Array.isArray(r) ? r : []);
    } catch {
      setError("Mahalleler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    if (!needle) return rows;
    return rows.filter((m) =>
      String(m.name || "").toLocaleLowerCase("tr").includes(needle)
    );
  }, [rows, q]);

  return (
    <div className="page-container ye-root">
      <header className="ye-head">
        <div>
          <h1 className="ye-title">Mahalleler</h1>
          <p className="ye-subtitle">
            Başvuru ve harita verisinde kullanılan mahalle kayıtlarını yönetin.
          </p>
        </div>
        {canCreate && (
          <button
            className="btn-md btn-md-primary ye-new-btn"
            onClick={() => setEditing({})}
          >
            <PlusIcon width={18} height={18} />
            Yeni Mahalle
          </button>
        )}
      </header>

      <div className="ye-filters">
        <div className="ye-search">
          <MagnifyingGlassIcon width={18} height={18} className="ye-search-icon" />
          <input
            className="md-input ye-search-input"
            placeholder="Mahalle adı ara"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Mahalle ara"
          />
        </div>
      </div>

      {loading ? (
        <div className="ye-state">Yükleniyor…</div>
      ) : error ? (
        <div className="ye-state ye-state-error">
          {error}
          <button className="btn-md btn-md-ghost" onClick={load}>
            Tekrar dene
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ye-state">
          {rows.length === 0 ? "Henüz mahalle yok." : "Eşleşen mahalle bulunamadı."}
        </div>
      ) : (
        <ul className="ye-list">
          {filtered.map((m) => (
            <li key={m.id}>
              <button className="md-card ye-item" onClick={() => setEditing(m)}>
                <span className="ye-item-main">
                  <span className="ye-item-name">
                    <MapPinIcon
                      width={15}
                      height={15}
                      style={{ verticalAlign: "-2px", marginRight: 6 }}
                    />
                    {m.name}
                  </span>
                  <span className="ye-item-sub">#{m.id}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <MahalleModal
          mahalle={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function MahalleModal({ mahalle, onClose, onSaved }) {
  const { can } = usePermissions();
  const isEdit = !!mahalle;
  const canUpdate = can("neighborhoods.update"); // REVIEW #6: update gate eksikti
  const canDelete = isEdit && can("neighborhoods.delete");

  const [name, setName] = useState(mahalle?.name || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      if (isEdit) await updateNeighborhood(mahalle.id, { name });
      else await createNeighborhood({ name });
      onSaved();
    } catch (ex) {
      setErr(backendError(ex, "Mahalle kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    setErr("");
    try {
      await deleteNeighborhood(mahalle.id);
      onSaved();
    } catch (ex) {
      setErr(backendError(ex, "Mahalle silinemedi."));
      setSaving(false);
    }
  };

  return (
    <div className="ye-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="ye-modal md-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="md-card-header">
          <h2 className="md-card-title">
            {isEdit ? "Mahalleyi Düzenle" : "Yeni Mahalle"}
          </h2>
        </div>
        <form className="ye-form" onSubmit={submit} autoComplete="off">
          {err && <div className="ye-alert">{err}</div>}
          <label className="ye-field">
            <span>Mahalle adı</span>
            <input
              className="md-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="örn: Cumhuriyet Mah."
              required
              autoFocus
            />
          </label>

          {confirmDelete ? (
            <div className="ye-alert">
              Bu mahalle silinsin mi? Kalıcı silinir. Bağlı başvuruların ve bu
              mahalleye bağlı idari sınır kaydının mahalle bağı kopar
              (başvurular/sınırlar silinmez, bağ NULL olur).
              <div className="ye-modal-actions">
                <button
                  type="button"
                  className="btn-md btn-md-ghost"
                  onClick={() => setConfirmDelete(false)}
                  disabled={saving}
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  className="btn-md btn-md-danger"
                  onClick={doDelete}
                  disabled={saving}
                >
                  {saving ? "Siliniyor…" : "Evet, sil"}
                </button>
              </div>
            </div>
          ) : (
            <div className="ye-modal-actions ye-modal-actions-split">
              {canDelete ? (
                <button
                  type="button"
                  className="btn-md btn-md-ghost ye-danger-text"
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                >
                  Sil
                </button>
              ) : (
                <span />
              )}
              <div className="ye-modal-actions">
                <button
                  type="button"
                  className="btn-md btn-md-ghost"
                  onClick={onClose}
                  disabled={saving}
                >
                  {isEdit && !canUpdate ? "Kapat" : "Vazgeç"}
                </button>
                {(!isEdit || canUpdate) && (
                  <button
                    type="submit"
                    className="btn-md btn-md-primary"
                    disabled={saving}
                  >
                    {saving ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
